// src/redux/modules/auth/hooks/useAuth.ts
import {
    useChangePasswordMutation,
    useLoginMutation,
    useRegisterMutation,
    useResetPasswordMutation,
} from '@/rtkQuery/features/services/auth/authApi.slice';

import { AuthService } from '@/rtkQuery/features/services/auth/authService';
import { tokenService } from '@/rtkQuery/features/services/auth/tokenService';
import type { LoginRequest, RegisterRequest, ChangePasswordRequest, ResetPasswordRequest } from '@/rtkQuery/features/models/auth.model';

type SuccessMessageOptions = { success?: string };

export const useAuthService = () => {
    const service = new AuthService();

    const [
        triggerLogin,
        { data: loginData, error: loginError, isLoading: isLoginLoading, isSuccess: isLoginSuccess, isError: isLoginError },
    ] = useLoginMutation();
    const [
        triggerRegister,
        { data: registerData, error: registerError, isLoading: isRegisterLoading, isSuccess: isRegisterSuccess, isError: isRegisterError },
    ] = useRegisterMutation();

    const [
        triggerChangePassword,
        {
            data: changePasswordData,
            error: changePasswordError,
            isLoading: isChangePasswordLoading,
            isSuccess: isChangePasswordSuccess,
            isError: isChangePasswordError,
        },
    ] = useChangePasswordMutation();

    const [
        triggerResetPassword,
        {
            data: resetPasswordData,
            error: resetPasswordError,
            isLoading: isResetPasswordLoading,
            isSuccess: isResetPasswordSuccess,
            isError: isResetPasswordError,
        },
    ] = useResetPasswordMutation();

    const loginStatus = isLoginLoading
        ? 'loading'
        : isLoginError
            ? 'error'
            : isLoginSuccess
                ? 'success'
                : 'idle';

    const registerStatus = isRegisterLoading
        ? 'loading'
        : isRegisterError
            ? 'error'
            : isRegisterSuccess
                ? 'success'
                : 'idle';

    return {
        login: (payload: LoginRequest, messages?: SuccessMessageOptions) =>
            service.login(triggerLogin, payload, messages),

        register: (payload: RegisterRequest, messages?: SuccessMessageOptions) =>
            service.register(triggerRegister, payload, messages),

        logout: () => service.logout(),

        changePassword: (
            payload: ChangePasswordRequest,
            messages?: SuccessMessageOptions
        ) => service.changePassword(triggerChangePassword, payload, messages),

        resetPassword: (payload: ResetPasswordRequest, messages?: SuccessMessageOptions) =>
            service.resetPassword(triggerResetPassword, payload, messages),


        isAuthenticated: tokenService.hasValidTokens(),
        isSessionValid:
            tokenService.hasValidTokens() && !tokenService.isAccessTokenExpired(),

        accessToken: tokenService.getAccessToken(),
        refreshToken: tokenService.getRefreshToken(),

        loginResponse: loginData,
        loginError,
        loginIsLoading: isLoginLoading,
        loginIsSuccess: isLoginSuccess,
        loginIsError: isLoginError,
        loginStatus,
        loginErrorMessage:
            (loginError as any)?.meta?.error || (loginError as any)?.data?.message,

        registerResponse: registerData,
        registerError,
        registerIsLoading: isRegisterLoading,
        registerIsSuccess: isRegisterSuccess,
        registerIsError: isRegisterError,
        registerStatus,
        registerErrorMessage:
            (registerError as any)?.meta?.error ||
            (registerError as any)?.data?.message,


        changePasswordResponse: changePasswordData,
        changePasswordError,
        changePasswordIsLoading: isChangePasswordLoading,
        changePasswordIsSuccess: isChangePasswordSuccess,
        changePasswordIsError: isChangePasswordError,
        changePasswordStatus: isChangePasswordLoading
            ? 'loading'
            : isChangePasswordError
                ? 'error'
                : isChangePasswordSuccess
                    ? 'success'
                    : 'idle',
        changePasswordErrorMessage:
            (changePasswordError as any)?.meta?.error ||
            (changePasswordError as any)?.data?.message,

        resetPasswordResponse: resetPasswordData,
        resetPasswordError,
        resetIsLoading: isResetPasswordLoading,
        resetIsSuccess: isResetPasswordSuccess,
        resetIsError: isResetPasswordError,
        resetStatus: isResetPasswordLoading
            ? 'loading'
            : isResetPasswordError
                ? 'error'
                : isResetPasswordSuccess
                    ? 'success'
                    : 'idle',
        resetErrorMessage:
            (resetPasswordError as any)?.meta?.error ||
            (resetPasswordError as any)?.data?.message,
    };
};
